'use client'

import {
    AnimatePresence,
    animate,
    motion,
    useMotionValue,
    useTransform,
    useWillChange,
} from 'motion/react'
import { useLocale, useTranslations } from 'next-intl'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMeasure } from 'react-use'
import { Input } from '@/components/input/input'
import { LoadingSpinner } from '@/components/loading-spinner'
import { RestaurantsSearchPlaceholder } from '@/features/gdpr/placeholders/restaurants-search-placeholder'
import { AddressSearchConsentGate } from '@/features/google-places/address-search-consent-gate'
import { createLoadAddressHandler } from '@/features/google-places/google-places-address-helpers'
import { useGooglePlacesSelect } from '@/features/google-places/google-places-hooks'
import { GooglePlacesOption } from '@/features/google-places/google-places-option'
import { IGooglePlacesSelectOption } from '@/features/google-places/google-places-types'
import { Locale } from '@/setup/locales'
import { IOption } from '@/typings/option'
import { debounce } from '@/utils/functions/debounce'
import { noOp } from '@/utils/functions/noOp'
import { run } from '@/utils/run'
import { cn } from '@/utils/styles/cn'

export enum GoogleAddressTag {
    CAFE = 'Cafe',
    RESTAURANT = 'Restaurant',
}

export interface IGoogleAddress {
    photos?: {
        getUrl: (options: { maxWidth: number; maxHeight: number }) => string
        width: number
        height: number
    }[]
    name?: string
    country: string
    zip: string
    city: string
    street: string
    streetNumber: string
    placeId?: string
    lat: number
    lng: number
    priceLevel?: number
    tags?: GoogleAddressTag[]
}

export type IGoogleAddressOption = IOption<IGoogleAddress>

interface IGooglePlacesRestaurantsSearchProps {
    label?: string
    placeholder?: string
    onSelect?: (addressOption: IGoogleAddressOption | null) => void | Promise<void>
    showOptions?: boolean
    autoFocus?: boolean
    inputClassName?: string
    containerClassName?: string
}

const MAX_SEARCH_RESULTS = 5

export const GooglePlacesRestaurantsSearch = (props: IGooglePlacesRestaurantsSearchProps) => (
    <AddressSearchConsentGate
        placeholder={(onOpen) => (
            <RestaurantsSearchPlaceholder
                onOpen={onOpen}
                label={props.label}
                placeholder={props.placeholder}
                inputClassName={props.inputClassName}
                containerClassName={props.containerClassName}
            />
        )}
    >
        <RestaurantsSearch {...props} />
    </AddressSearchConsentGate>
)

const RestaurantsSearch = ({
    label,
    onSelect: propsOnSelect = noOp,
    placeholder,
    showOptions = true,
    autoFocus,
    inputClassName,
    containerClassName,
}: IGooglePlacesRestaurantsSearchProps) => {
    const _t = useTranslations()
    const [highlightedIndex, setHighlightedIndex] = useState<number | null>(0)

    const [isLoading, setIsLoading] = useState(false)

    const willChange = useWillChange()

    const [inputValue, setInputValue] = useState('')

    const [googlePlacesOptions, setGooglePlacesOptions] = useState<
        IGooglePlacesSelectOption[] | null
    >(null)

    const resultsOpacity = useMotionValue(0)
    const loadingSpinnerOpacity = useTransform(resultsOpacity, [0, 1], [1, 0])

    const { loadSelectOptions, loadPlace } = useGooglePlacesSelect({ debounceWait: 250 })

    const commitId = useRef(0)

    const fetchOptions = useCallback(
        (nextSearchValue: string) => {
            loadSelectOptions(
                nextSearchValue,
                ++commitId.current,
                (freshOptions, loadedCommitId) => {
                    // This is to avoid displaying results from a previous search
                    if (loadedCommitId !== commitId.current) return

                    animate(resultsOpacity, freshOptions?.length ? 1 : 0)
                    setGooglePlacesOptions(freshOptions)
                }
            )
        },
        [resultsOpacity, loadSelectOptions]
    )

    const debouncedFetch = useMemo(
        () => debounce((nextSearchValue: string) => fetchOptions(nextSearchValue), 150),
        [fetchOptions]
    )

    const onValueChange = useCallback(
        (nextValue: string) => {
            setInputValue(nextValue)
            animate(resultsOpacity, nextValue ? 0.2 : 0)

            if (highlightedIndex !== null) setHighlightedIndex(0)

            if (!nextValue) {
                setGooglePlacesOptions(null)
                return
            }

            const isFirstInput = inputValue === '' && nextValue.length === 1

            // When the user starts typing, we want to fetch the options immediately
            // so that the user gets direct feedback on the search and realizes, that the search is working
            // without a need to commit the input value by pressing enter or any other key
            if (isFirstInput) {
                fetchOptions(nextValue)
            } else {
                debouncedFetch(nextValue)
            }
        },
        [debouncedFetch, resultsOpacity, highlightedIndex, inputValue, fetchOptions]
    )

    const locale = useLocale()

    const loadAddress = useMemo(
        () =>
            createLoadAddressHandler(locale as Locale, loadPlace, (selectedOption) => {
                const promise = propsOnSelect(selectedOption)

                if (promise) {
                    setIsLoading?.(true)
                    promise.finally(() => setIsLoading?.(false))
                }
            }),
        [propsOnSelect, loadPlace, locale]
    )

    const onSelect = useCallback(
        (nextSelectedOption: IGooglePlacesSelectOption | null) => {
            loadAddress(nextSelectedOption)
            setGooglePlacesOptions(null)
        },
        [loadAddress]
    )

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            // Handle enter press when an option is highlighted
            if (event.key === 'Enter' && highlightedIndex !== null) {
                const selectedOption = googlePlacesOptions?.[highlightedIndex]

                if (selectedOption) onSelect(selectedOption)
            }

            if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                event.preventDefault()

                const optionsCount = Math.min(googlePlacesOptions?.length || 0, MAX_SEARCH_RESULTS)
                if (optionsCount === 0) return

                let newIndex: number | null = null

                if (highlightedIndex === null) {
                    newIndex = event.key === 'ArrowUp' ? optionsCount - 1 : 0
                } else {
                    newIndex =
                        event.key === 'ArrowUp'
                            ? (highlightedIndex - 1 + optionsCount) % optionsCount
                            : (highlightedIndex + 1) % optionsCount
                }

                setHighlightedIndex(newIndex)
            }
        },
        [googlePlacesOptions, highlightedIndex, onSelect]
    )

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [handleKeyDown])

    const containerRef = useRef<HTMLDivElement>(null)

    const [listRef, { height: listHeight }] = useMeasure<HTMLDivElement>()

    return (
        <Fragment>
            <div
                ref={containerRef}
                className={cn('relative w-full md:w-auto flex', containerClassName)}
            >
                <Input
                    autoFocus={autoFocus}
                    label={label}
                    value={inputValue}
                    onValueChange={onValueChange}
                    placeholder={placeholder}
                    styleType="white"
                    type="search"
                    isLoading={isLoading}
                    shape="pill"
                    inputClassName={cn(
                        'min-h-[54px] md:max-w-[350px] min-w-[350px] w-full border-[2px]',
                        inputClassName
                    )}
                    containerClassName="w-full"
                />

                <AnimatePresence>
                    {Boolean(googlePlacesOptions) && Boolean(inputValue) && showOptions && (
                        <Fragment>
                            <div
                                onClick={() => {
                                    setGooglePlacesOptions(null)
                                }}
                                className="fixed left-0 right-0 top-0 bottom-0 min-h-dvh min-w-[100vw] z-[1]"
                            />

                            <motion.div
                                style={{ willChange }}
                                initial={{
                                    scale: 1,
                                    height: 0,
                                }}
                                animate={{
                                    scale: 1,
                                    height: listHeight || undefined,
                                }}
                                exit={{
                                    opacity: 0,
                                }}
                                transition={{
                                    opacity: { duration: 0.05, ease: 'easeInOut' },
                                    scale: { type: 'spring', stiffness: 1000, damping: 70 },
                                    height: {
                                        duration: 0.4,
                                        type: 'spring',
                                        stiffness: 400,
                                        damping: 40,
                                    },
                                }}
                                className="bg-white absolute top-[72px] rounded-l border-black overflow-hidden w-full transition-opacity
                                    shadow-custom z-[2]"
                            >
                                <motion.div ref={listRef} className="min-h-fit w-full relative">
                                    <motion.div
                                        style={{ opacity: loadingSpinnerOpacity }}
                                        className="transform-center z-[1] transition-opacity"
                                    >
                                        <LoadingSpinner color="black" size={24} />
                                    </motion.div>

                                    <motion.div
                                        style={{ opacity: resultsOpacity }}
                                        className="w-full transition-opacity p-2"
                                    >
                                        {run(() => {
                                            const isEmpty = !googlePlacesOptions?.length

                                            if (isEmpty)
                                                return (
                                                    <div className="flex items-center h-[73px] px-10 py-0 gap-x-4 bg-white relative w-full rounded-m">
                                                        No results found
                                                    </div>
                                                )

                                            return googlePlacesOptions?.map((option, index) => {
                                                if (index > MAX_SEARCH_RESULTS - 1) return null

                                                return (
                                                    <GooglePlacesOption
                                                        highlighted={highlightedIndex === index}
                                                        key={option.value.place_id}
                                                        option={option}
                                                        onClick={() => onSelect(option)}
                                                        setIsLoading={setIsLoading}
                                                    />
                                                )
                                            })
                                        })}
                                    </motion.div>
                                </motion.div>
                            </motion.div>
                        </Fragment>
                    )}
                </AnimatePresence>
            </div>
        </Fragment>
    )
}
